import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import {
  useNavigateWithBreadcrumbs,
  useSyncCurrentOrganization,
} from '../../hooks';
import { ROUTES } from '../../constants';
import i18n from '../../utilities/i18n';
import SubjectPageHeader from './subjectComponents/SubjectHeader';
import { Sensor } from '../../models';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LinkText,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import DateCellRenderer from '../table/DateCellRenderer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSubjectNoteMutateOptions,
  devicesBySubjectQueryOptions,
  organizationQueryOptions,
  sensorsQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
  subjectQueryOptions,
} from '../../queryOptions';
import CreateUserNoteModal from '../modal/CreateUserNoteModal';
import { EntityType, UserNote } from 'adc-sirius-module-common';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import { QrCodePopup } from '../qrCode/QrCodePopup';
import { DevicesTable } from '../devices/DevicesTable';
import { subjectCapabilitiesQueryOptions } from '../../queryOptions/capability';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';

/**
 * Subject component that displays the details of a specific subject.
 * @returns {React.JSX.Element}
 */
export default function Subject() {
  const queryClient = useQueryClient();

  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToDevice = (deviceKey: string, appName: string) => {
    navigateWithBreadcrumbs(
      ROUTES.DEVICES.DETAIL.replace(':deviceKey', deviceKey),
      appName,
      subject.data?.name || i18n.t('Page_Subject.title'),
    );
  };

  const navigateToSensor = (sensorId: string, sensorSerialNumber: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SENSORS.DETAIL.replace(':sensorKey', sensorId),
      sensorSerialNumber,
      subject.data?.name || i18n.t('Page_Subject.title'),
    );
  };

  const { subjectKey } = useParams();

  const subject = useQuery(subjectQueryOptions({ key: subjectKey }));

  const { data: capabilities } = useQuery(
    subjectCapabilitiesQueryOptions({ key: subjectKey }),
  );

  const errorMessage = useErrorMessageOnEntityView(
    subject.error,
    'Page_Subject.load_error',
  );

  const site = useQuery(siteQueryOptions({ key: subject.data?.siteKey }));
  const study = useQuery(studyQueryOptions({ key: site.data?.studyKey }));

  useSyncCurrentOrganization(study.data?.organizationKey || '');

  const organization = useQuery(
    organizationQueryOptions({ key: study.data?.organizationKey }),
  );

  const sensors = useQuery(
    sensorsQueryOptions({ subjectKey }, { enabled: !!subjectKey }),
  );

  const devices = useQuery(
    devicesBySubjectQueryOptions(
      { key: subjectKey },
      { enabled: !!subjectKey && !!capabilities?.canGetDevices },
    ),
  );

  const createNote = useMutation(
    createSubjectNoteMutateOptions({
      onSuccess: (updatedSubject) => {
        // update only the notes in the cached subject data
        queryClient.setQueryData(
          subjectQueryOptions({ key: subjectKey }).queryKey,
          (cachedSubject) => {
            if (!cachedSubject) {
              return updatedSubject;
            }

            return {
              ...cachedSubject,
              userNotes: updatedSubject.userNotes || cachedSubject.userNotes,
              updatedOn: updatedSubject.updatedOn || cachedSubject.updatedOn,
              updatedBy: updatedSubject.updatedBy || cachedSubject.updatedBy,
              updateReason:
                updatedSubject.updateReason || cachedSubject.updateReason,
            };
          },
        );
      },
    }),
  );

  const sensorsTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.serialNumber'), dataKey: 'serialNumber' },
    { label: i18n.t('Entity.condition'), dataKey: 'conditionCode' },
    {
      label: i18n.t('Entity.lifecount'),
      dataKey: 'lifecount',
      getSortValue: (sensor: Sensor) => sensor.analyteCounts[0]?.current ?? 0,
    },
    { label: i18n.t('Entity.activatedOn'), dataKey: 'activationTime' },
  ];

  const notesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.timestamp'), dataKey: 'timestamp' },
    { label: i18n.t('Entity.note'), dataKey: 'note' },
    { label: i18n.t('Entity.createdBy'), dataKey: 'user_display_name' },
  ];

  const sensorsTableCellRenderer = (dataKey: string, sensor: Sensor) => {
    if (dataKey === 'lifecount') {
      return (
        <Table.TextCell key={dataKey}>
          {sensor.analyteCounts[0]?.current ?? 0}
        </Table.TextCell>
      );
    } else if (dataKey === 'activationTime') {
      return <DateCellRenderer value={sensor.activationTime} key={dataKey} />;
    } else if (dataKey === 'serialNumber') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSensor(sensor.key, sensor.serialNumber)}
            type="$bodySmall.default"
            testID={`sensor-details-link-${sensor.key}`}
          >
            {sensor[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{sensor[dataKey]}</Table.TextCell>;
    }
  };

  const notesTableCellRenderer = (dataKey: string, note: UserNote) => {
    if (dataKey === 'timestamp') {
      return <DateCellRenderer value={note.timestamp} key={dataKey} />;
    } else {
      return <Table.TextCell key={dataKey}>{note[dataKey]}</Table.TextCell>;
    }
  };

  const qrCodeError = useMemo(() => {
    if (!organization.isPending && !organization.data) {
      return true;
    }

    if (!study.isPending && !study.data) {
      return true;
    }

    if (!site.isPending && !site.data) {
      return true;
    }

    if (!subject.isPending && !subject.data) {
      return true;
    }

    return false;
  }, [
    site.isPending,
    site.data,
    study.isPending,
    study.data,
    organization.isPending,
    organization.data,
    subject.isPending,
    subject.data,
  ]);

  return (
    <VStack gap={'$4'} height={'100%'}>
      {subject.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Subject.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="subject-error-banner"
        />
      ) : null}

      {subject.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="subject-loading" />
        </VStack>
      ) : null}

      {subject.isSuccess ? (
        <VStack gap="$4">
          <SubjectPageHeader
            subject={subject.data}
            isReadonly={!capabilities?.canUpdateSubject}
          >
            <QrCodePopup
              organizationIdentifier={organization.data?.name || ''}
              organizationKey={organization.data?.key || ''}
              studyIdentifier={study.data?.name || ''}
              siteIdentifier={site.data?.name}
              subjectIdentifier={subject.data.name}
              studyKey={study.data?.key || ''}
              siteKey={site.data?.key}
              subjectKey={subject.data.key}
              error={qrCodeError}
              loading={
                study.isPending || organization.isPending || site.isPending
              }
            />
          </SubjectPageHeader>
          <DynamicTable
            testID="sensors-table"
            isLoading={sensors.isPending}
            hasLoadingError={sensors.isError}
            title={i18n.t('Page_Subject.table_sensors_title')}
            description={i18n.t('Page_Subject.table_sensors_description')}
            columnData={sensorsTableColumnData}
            rowData={sensors.data || []}
            cellRenderer={sensorsTableCellRenderer}
          />
          {capabilities?.canGetDevices ? (
            <DevicesTable
              title={i18n.t('Page_Subject.table_devices_title')}
              description={i18n.t('Page_Subject.table_devices_description')}
              isLoading={devices.isPending}
              hasLoadingError={devices.isError}
              devices={devices.data || []}
              navigateToDevice={navigateToDevice}
              showUploadHistory
              toolbarRightContent={
                capabilities.canManageDeviceAssignments ? (
                  <AssignDeviceModal
                    entityKey={subject.data.key}
                    entityName={subject.data.name}
                    entityType={EntityType.StudySubject}
                    organizationKey={study.data?.organizationKey || ''}
                    trigger={
                      <Button
                        size="medium"
                        label={i18n.t('Page_Subject.assign_device')}
                        onPress={() => {}}
                        disabled={
                          devices.isPending ||
                          study.isPending ||
                          !study.data?.organizationKey
                        }
                        testID="assign-device-button"
                      />
                    }
                  />
                ) : undefined
              }
            />
          ) : null}
          <DynamicTable
            testID="subject-notes-table"
            isLoading={subject.isLoading}
            hasLoadingError={subject.isError}
            title={i18n.t('Page_Subject.table_notes_title')}
            description={i18n.t('Page_Subject.table_notes_description')}
            columnData={notesTableColumnData}
            rowData={subject.data.userNotes || []}
            cellRenderer={notesTableCellRenderer}
            toolbarRightContent={
              capabilities?.canUpdateSubject ? (
                <CreateUserNoteModal
                  trigger={
                    <Button
                      size="medium"
                      label={i18n.t('Page_Subject.add_note')}
                      onPress={() => {}}
                      disabled={createNote.isPending}
                      testID="add-note-button"
                    />
                  }
                  onSuccess={async (newNote) => {
                    await createNote.mutateAsync({
                      subjectKey: subject.data.key,
                      noteAddRequest: newNote,
                    });
                  }}
                />
              ) : undefined
            }
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
